import { useMemo } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const WEEKS = 8;

const mondayOf = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  return date;
};

const buildWeeks = (orders) => {
  const start = mondayOf(new Date());
  const weeks = [];
  for (let i = WEEKS - 1; i >= 0; i--) {
    const ws = new Date(start);
    ws.setDate(ws.getDate() - i * 7);
    weeks.push({
      key: ws.getTime(),
      label: ws.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      revenue: 0,
      orders: 0,
    });
  }
  orders.forEach((o) => {
    if (!o.created_at) return;
    const key = mondayOf(new Date(o.created_at)).getTime();
    const week = weeks.find((w) => w.key === key);
    if (week) {
      week.revenue += (o.amount_total || 0) / 100;
      week.orders += 1;
    }
  });
  return weeks;
};

const SnapshotTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const w = payload[0].payload;
  return (
    <div className="bg-[#FFFDF9] border border-[rgba(58,42,37,0.12)] rounded-lg px-3 py-2 shadow-sm">
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#9A8D85]">Week of {w.label}</p>
      <p className="text-sm text-[#2B1F1B] mt-1">${w.revenue.toFixed(2)} · {w.orders} order{w.orders === 1 ? "" : "s"}</p>
    </div>
  );
};

const WowDelta = ({ current, previous }) => {
  if (previous === 0 && current === 0) {
    return (
      <span className="flex items-center gap-1 text-[#9A8D85]" data-testid="sales-wow-delta">
        <Minus size={13} /> No sales yet
      </span>
    );
  }
  if (previous === 0) {
    return (
      <span className="flex items-center gap-1 text-[#2E7D32]" data-testid="sales-wow-delta">
        <TrendingUp size={13} /> New sales this week
      </span>
    );
  }
  const pct = ((current - previous) / previous) * 100;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`flex items-center gap-1 ${up ? "text-[#2E7D32]" : "text-[#C62828]"}`} data-testid="sales-wow-delta">
      <Icon size={13} /> {up ? "+" : ""}{pct.toFixed(0)}% vs last week
    </span>
  );
};

export const SalesSnapshot = ({ orders }) => {
  const weeks = useMemo(() => buildWeeks(orders || []), [orders]);
  const current = weeks[weeks.length - 1];
  const previous = weeks[weeks.length - 2];

  return (
    <div className="bg-[#FFFDF9] rounded-xl border border-[rgba(58,42,37,0.08)] p-6" data-testid="sales-snapshot">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-mono-accent uppercase tracking-[0.15em] text-[#9A8D85]">
          Weekly sales — last {WEEKS} weeks
        </span>
        <span className="text-xs font-light">
          <WowDelta current={current.revenue} previous={previous.revenue} />
        </span>
      </div>
      <div className="h-40 mt-4" data-testid="sales-snapshot-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={weeks} margin={{ top: 4, right: 0, left: -18, bottom: 0 }}>
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10, fill: "#9A8D85" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 10, fill: "#9A8D85" }}
              tickFormatter={(v) => `$${v}`}
              allowDecimals={false}
              width={52}
            />
            <Tooltip content={<SnapshotTooltip />} cursor={{ fill: "rgba(201,164,92,0.08)" }} />
            <Bar dataKey="revenue" fill="#C9A45C" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
